import { useRecoilValue } from "recoil"

import { currentUserState } from "~/containers/Proposal/state/user/user-state"
import { Permission } from "~/graphql/client"

/**
 * Hook to determine if the current user has permission to edit proposals.
 * Uses an optimistic approach: returns true if user has ANY edit permission.
 *
 * The backend CASL authorization (gateway/src/casl/casl-ability.factory.ts)
 * remains the source of truth and will enforce actual permissions on mutations.
 *
 * MAINTENANCE: When permission rules change in CASL, this list must be updated.
 *
 * @returns {boolean} Whether the user should see edit UI (backend enforces actual access)
 */
export const useCanEditProposal = (): boolean => {
  const { permissions } = useRecoilValue(currentUserState)

  const editPermissions: Permission[] = [
    // Admin-level permissions (can edit ANY proposal)
    Permission.admin,
    Permission.vision_admin,
    Permission.vision_designer_user,
    Permission.vision_proposal_admin,

    // Conditional permissions (can edit specific proposals based on ownership/assignment)
    Permission.proposal_creation,
    Permission.mentorship_assignment,
    Permission.org_mentor_access,
    Permission.enterprise_admin,
    Permission.enterprise_admin_pro,
    Permission.enterprise_super_admin,
    Permission.enterprise_consultant,
  ]

  return editPermissions.some((permission) => permissions.includes(permission))
}
